/* eslint-disable no-nested-ternary */
import React from 'react';
import { Link } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { HeaderLink, Navigation } from '../types/app.header';
import logo from '../assets/img/logo.svg';
import { AppState } from '../store/store.types';
import { AuthenticationActionTypes } from '../modules/authentication/types/authentication.types';
import { getSupplier, performLogout, storeUserChanges } from '../modules/authentication/actions/authentication.action';
import { UserModel } from '../api/authentication/authentication.types';
import { LOCALSTORAGE_ACTIVE_SUPPLIER, LOCALSTORAGE_SUPPLIERS, STORED_PRODUCT_ID } from '../types/app.index';
import { userCanAccessComponent } from '../helpers/authentication.helper';
import { STORE_DESK_GROUP_ID } from '../modules/rates/types/rateplan.types';
import { WithTranslation, withTranslation } from 'react-i18next';
import { SupplierModel } from '../api/admin/admin.types';
import { AdminApi } from '../api/api.apiclient';
import Markdown from 'markdown-to-jsx';

interface HeaderProps extends WithTranslation, RouteComponentProps {
  navigation: Navigation;
  logout: () => void;
  user: UserModel | null;
  supplier: SupplierModel | null;
  getSupplier: () => void;
  storeUserChanges: (user: UserModel) => void;
}

class Header extends React.Component<HeaderProps, {}> {

  readonly state = {
    userMenuOpen: false,
    supplierMenuOpen: false,
    supplierOptions: [] as SupplierModel[],
    activeSupplier: { name: 'supplier', code: 'supplier' }
  };

  componentDidMount() {
    const storedSuppliersString = localStorage.getItem(LOCALSTORAGE_SUPPLIERS) ?? null;
    let storedSuppliers: SupplierModel[] | null = null;
    const storedActiveSupplierString = localStorage.getItem(LOCALSTORAGE_ACTIVE_SUPPLIER) ?? null;
    let storedActiveSupplier: SupplierModel | null = null;

    if (storedSuppliersString) {
      storedSuppliers = JSON.parse(storedSuppliersString);
      this.props.getSupplier();
      this.setState({ supplierOptions: storedSuppliers });
    }

    if (storedActiveSupplierString) {
      storedActiveSupplier = JSON.parse(storedActiveSupplierString);
      this.setState({ activeSupplier: storedActiveSupplier });
    }

  }

  getNavigation() {
    const { t } = this.props;
    const path = this.props.match.path;
    return this.props.navigation.links.map((l) => {
      if (this.props.user && userCanAccessComponent(this.props.user, l.roles)) {
        return (
          <li key={l.route} className={`nav-item${(path.indexOf(l.route) !== -1 && ' active') || ''}`}>
            <Link
              to={l.route}
              className="nav-link"
            >
              {t(l.title as keyof Translation)}
            </Link>
          </li>
        );
      }
    });
  }

  getSubnav() {
    const { t } = this.props;
    const path = this.props.match.path;
    const main = this.props.navigation.links.find((l) => l.route === path);
    if (main?.subnav) {
      const allowedSubnavs: HeaderLink[] = [];

      main.subnav.forEach((subnavElement) => {
        if (this.props.user && userCanAccessComponent(this.props.user, subnavElement.roles)) {
          allowedSubnavs.push(subnavElement);
        }
      });
      return (
        <nav className="subnav">
          <ul>
            {
              allowedSubnavs.map((l) => (
                <li key={l.route} className={(this.props.location.pathname === l.route && 'active') || ''}>
                  <Link to={l.route}>{t(l.title as keyof Translation)}</Link>
                </li>
              ))
            }
          </ul>
        </nav>
      );
    }
    return null;
  }

  getNotifications = () => {
    const notifications: JSX.Element[] = [];
    if (this.props.supplier?.notifications?.length) {
      this.props.supplier?.notifications?.forEach((supplierNotification) => {
        if (!this.props.user?.seenNotifications || this.props.user?.seenNotifications.indexOf(supplierNotification.id) === -1) {
          notifications.push(
            <div
              className={`d-flex flex-row notification ${supplierNotification.important ? 'warning' : 'info'}`}>
              <div key={supplierNotification.id} className="flex-fill">
                <div className="text-bold">{supplierNotification.title}</div>
                <Markdown options={{ disableParsingRawHTML: true }}>{supplierNotification.text}</Markdown>
              </div>
              {!supplierNotification.important ? (
                <div className="d-flex justify-content-end align-items-start">
                  <button
                    className="close-btn" type="button"
                    onClick={() => this.markNotificationSeen(supplierNotification.id)}> &#10005;</button>
                </div>) : ''}
            </div>
          );
        }
      });
    }
    return notifications;
  };

  markNotificationSeen = (id: number) => {
    let userSeenNotifications = this.props.user?.seenNotifications;
    if (!userSeenNotifications) {
      userSeenNotifications = [];
    }
    userSeenNotifications.push(id);
    AdminApi.notifications.userHasSeenNotification(id)
      .then(() => this.props.storeUserChanges({
        ...this.props.user,
        seenNotifications: userSeenNotifications
      } as UserModel));

  };

  handleChange = async (event: any) => {
    const { value } = event.currentTarget;
    const supplier = this.state.supplierOptions.find((selected) => selected.code === value);
    this.setState({ activeSupplier: supplier });
    localStorage.setItem(LOCALSTORAGE_ACTIVE_SUPPLIER, JSON.stringify(supplier));
    localStorage.removeItem(STORED_PRODUCT_ID);
    localStorage.removeItem(STORE_DESK_GROUP_ID);

    if (window.location.pathname.includes('view')) {
      window.location.replace('/reservations');
      window.location.reload();
    } else window.location.reload();
  };

  onLogOut = async () => {
    this.props.logout();
  };

  render() {
    const { t } = this.props;

    return (
      <header>
        <nav className="navbar">
          <div>
            <Link to="/">
              <img alt="logo" className="logo" src={logo}></img>
            </Link>
          </div>
          <div className="top-links ml-2">
            <ul className="list-unstyled">
              {this.getNavigation()}
            </ul>
          </div>
          {this.props.user && (
            <>
              <div className="supplier-menu">
                <Dropdown inNavbar isOpen={this.state.supplierMenuOpen}
                          toggle={() => this.setState({ supplierMenuOpen: !this.state.supplierMenuOpen })}>
                  <DropdownToggle caret nav>
                    {this.state.activeSupplier && this.state.activeSupplier.name}
                  </DropdownToggle>
                  <DropdownMenu tag="ul" className="to-very-front">
                    {this.state.supplierOptions.sort((i, d) => ((i.name < d.name) ? -1 : (i.name > d.name) ? 1 : 0)).map((supplier: SupplierModel) => (
                      <DropdownItem key={supplier.code} onClick={this.handleChange}
                                    value={supplier.code}>{supplier.name}</DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
              <div className="user-menu">
                <Dropdown inNavbar isOpen={this.state.userMenuOpen}
                          toggle={() => this.setState({ userMenuOpen: !this.state.userMenuOpen })}>
                  <DropdownToggle caret nav>
                    {this.props.user && (`${this.props.user.firstName} ${this.props.user.lastName}`)}
                  </DropdownToggle>
                  <DropdownMenu tag="ul">
                    <DropdownItem tag="li" onClick={this.onLogOut}>{t('components.header.log_out')}</DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </>
          )}
        </nav>
        {this.getSubnav()}
        {this.getNotifications()}
      </header>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  token: state.authentication.token,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier
});
const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, AuthenticationActionTypes>) => ({
  logout: () => dispatch(performLogout()),
  getSupplier: () => dispatch(getSupplier()),
  storeUserChanges: (user: UserModel) => dispatch(storeUserChanges(user))
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(Header));
